/**
 * 確認ページ /n/:token（#233 3b、docs/93 §15.6）：{vendor}がログインせずに{paymentNotice}を確かめて「確認」を押す1枚（承諾ページ /o/ と同じ土台）。
 *   アプリの上の帯とメニューは出さない（ログイン前の画面と同じくライト・製品の既定の色）。スマホ前提＝縦1列・押す所 56px
 *   頭：「発行：自社名」・題（{paymentNotice}）・番号・件名・合計（税込）・発行日と確認の期限 → 「PDF 保存」 → 紙（ページの画像＝pdf.js）
 *   下に固定：［確認］と小さく「誤りがある ›」（理由の欄 200字 → 発行した会社に通知・記録）
 *   確認のあと：題の右に「確認済 MM/DD」・「確認しました（MM/DD HH:MM）」・記録番号。期限を過ぎて確認済になったものは「期限を過ぎたため確認済になりました」
 *   誤りがあると連絡したあと：「誤りがあると連絡済みです」（押せない）
 *   無効・取消し・期限切れ（410）は止めの画面（発行した会社名）。無い鍵（404）は「ページが見つかりません」
 *   送った会社でログイン中の人は確かめ用に見るだけ（押せない＝API も 403）
 */
import { NOTICE_PUBLIC_MSG as M, NoticeDisputeRequest, PAYMENT_NOTICE_LIMITS, STOP_MSG, type NoticeConfirmResult, type PaymentNoticePublicPage } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { PageViewer, type ViewerPage, type Zoom } from "../../components/PageViewer.jsx";
import { StopScreen } from "../../components/StopScreen.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { renderPdf } from "../../lib/pdfPages.js";
import { PRELOGIN_PRODUCT, productInfo } from "../../lib/product.js";

class NoticeApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: { title?: string; code?: string; company?: string; state?: string },
  ) {
    super(body.title ?? "いま処理できませんでした");
  }
}
/** 公開の API（控えを使わない・リファラーを送らない。Cookie は同じサイトなので送る＝送った会社でログイン中かを API が見る） */
async function ncall(path: string, method: "GET" | "POST" = "GET", body: unknown = {}): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/n/${path}`, { method, credentials: "same-origin", referrerPolicy: "no-referrer", cache: "no-store", ...(method === "POST" ? { headers: { "content-type": "application/json" }, body: JSON.stringify(body) } : {}) });
  } catch {
    throw new NoticeApiError(0, { title: "つながりませんでした。電波の状態を確かめて、もう一度お試しください" });
  }
  if (!res.ok) {
    let b: NoticeApiError["body"] = {};
    try {
      b = (await res.json()) as NoticeApiError["body"];
    } catch {
      /* JSON でない答え */
    }
    throw new NoticeApiError(res.status, b);
  }
  return res;
}

const jst = (iso: string) => new Date(Date.parse(iso) + 9 * 3600 * 1000);
const ymdSlash = (iso: string) => {
  const t = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00Z`) : jst(iso);
  return `${t.getUTCFullYear()}/${String(t.getUTCMonth() + 1).padStart(2, "0")}/${String(t.getUTCDate()).padStart(2, "0")}`;
};
const mdTime = (iso: string) => {
  const t = jst(iso);
  return `${t.getUTCMonth() + 1}/${t.getUTCDate()} ${String(t.getUTCHours()).padStart(2, "0")}:${String(t.getUTCMinutes()).padStart(2, "0")}`;
};
const mdOnly = (iso: string) => {
  const t = jst(iso);
  return `${t.getUTCMonth() + 1}/${t.getUTCDate()}`;
};

/** リファラーを送らない・検索に出さない（Caddy も /n/* に付ける。開発の Vite 用に meta も） */
function useNoReferrer(title: string) {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "referrer";
    m.content = "no-referrer";
    const r = document.createElement("meta");
    r.name = "robots";
    r.content = "noindex, nofollow";
    document.head.append(m, r);
    const prev = document.title;
    document.title = title;
    return () => {
      m.remove();
      r.remove();
      document.title = prev;
    };
  }, [title]);
}

export function NoticeConfirmPage() {
  const { token = "" } = useParams();
  const qc = useQueryClient();
  const P = productInfo(PRELOGIN_PRODUCT);
  const key = ["notice-page", token];
  const q = useQuery({ queryKey: key, queryFn: async () => (await (await ncall(encodeURIComponent(token))).json()) as PaymentNoticePublicPage, retry: false, staleTime: Infinity });
  const d = q.data;
  useNoReferrer(d ? `${d.title} ${d.docNo}` : "");
  const [pages, setPages] = useState<ViewerPage[] | null>(null);
  const [pdfError, setPdfError] = useState<string | undefined>();
  const [zoom, setZoom] = useState<Zoom>("fit");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const [ask, setAsk] = useState<{ reason: string; error?: string | undefined } | null>(null);
  const ok = !!d;
  useEffect(() => {
    if (!ok) return;
    let alive = true;
    (async () => {
      try {
        const blob = await (await ncall(`${encodeURIComponent(token)}/pdf`)).blob();
        const got: ViewerPage[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          got[i - 1] = { n: i, url: p.url, width: p.w, height: p.h };
        });
        if (alive) setPages(got.filter(Boolean));
      } catch (e) {
        if (alive) setPdfError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [ok, token]);

  if (q.isError) {
    const e = q.error instanceof NoticeApiError ? q.error : null;
    if (e?.status === 410) return <StopScreen art="lock" title={e.body.state === "expired" ? STOP_MSG.noticeGone.expiredTitle : STOP_MSG.noticeGone.title} body={STOP_MSG.noticeGone.body(e.body.company ?? "")} testId="notice-gone" />;
    if (e?.status === 404) return <StopScreen art="question" title={STOP_MSG.deliveryNotFound.title} body={STOP_MSG.deliveryNotFound.body} testId="notice-notfound" />;
    return (
      <div className="dview" data-testid="notice-confirm">
        <ErrorText>{q.error instanceof Error ? q.error.message : String(q.error)}</ErrorText>
      </div>
    );
  }
  if (!d) return <div className="dview"><p className="hint">読み込んでいます…</p></div>;
  const say = (s: string) => s.replaceAll("{company}", d.company).replaceAll("{paymentNotice}", d.title);
  const confirm = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const r = (await (await ncall(`${encodeURIComponent(token)}/confirm`, "POST")).json()) as NoticeConfirmResult;
      qc.setQueryData(key, { ...d, confirmed: { at: r.at, recordNo: r.recordNo, method: "screen" } } satisfies PaymentNoticePublicPage);
    } catch (e) {
      if (e instanceof NoticeApiError && e.status === 409) void q.refetch();
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const dispute = async () => {
    if (!ask) return;
    const v = NoticeDisputeRequest.safeParse({ reason: ask.reason });
    if (!v.success) {
      setAsk({ ...ask, error: v.error.issues[0]?.message });
      return;
    }
    setBusy(true);
    try {
      const r = (await (await ncall(`${encodeURIComponent(token)}/dispute`, "POST", { reason: v.data.reason })).json()) as { at: string };
      qc.setQueryData(key, { ...d, disputed: { at: r.at } } satisfies PaymentNoticePublicPage);
      setAsk(null);
    } catch (e) {
      setAsk((a) => (a ? { ...a, error: e instanceof Error ? e.message : String(e) } : a));
      if (e instanceof NoticeApiError && e.status === 409) void q.refetch();
    } finally {
      setBusy(false);
    }
  };
  const done = d.confirmed ?? null;
  return (
    <div className="dview oaccept" data-testid="notice-confirm">
      <header className="dview-head">
        <p className="dview-kicker" data-testid="notice-from">{say(M.from)}</p>
        <div className="row" style={{ alignItems: "center", gap: 8 }}>
          <h1 className="dview-company" data-testid="notice-title">{d.title}</h1>
          {done ? <span className="chip ai" data-testid="notice-confirmed-chip">{M.confirmedChip.replace("{md}", mdOnly(done.at))}</span> : null}
          {d.disputed && !done ? <span className="chip shu" data-testid="notice-disputed-chip">{M.disputedChip}</span> : null}
        </div>
        <p className="dview-subject" data-testid="notice-subject">{d.docNo}　{d.subject}</p>
        <p className="oaccept-total">
          <span className="muted">{M.total}</span> <b className="num" data-testid="notice-total">{d.total.toLocaleString("ja-JP")}</b> 円
        </p>
        <p className="hint num">
          {M.issueDate} {ymdSlash(d.issueDate)}
          {!done && d.deadline ? `　${M.deadline} ${ymdSlash(d.deadline)}` : ""}
        </p>
      </header>
      <div className="row oaccept-links">
        <a className="btn" href={`/api/v1/n/${encodeURIComponent(token)}/pdf?dl=1`} rel="noreferrer" data-testid="notice-pdf">{M.pdf}</a>
      </div>
      <div className="oaccept-paper" data-testid="notice-paper">
        {pdfError ? <ErrorText>{pdfError}</ErrorText> : null}
        <PageViewer docKey={token} title={d.title} pages={pages ?? undefined} error={undefined} pageCount={d.pages} openUrl={`/api/v1/n/${encodeURIComponent(token)}/pdf`} zoom={zoom} onZoom={setZoom} compact label={d.title} />
      </div>
      {done ? (
        <div className="oaccept-done" data-testid="notice-confirmed">
          <b>{(done.method === "expired" ? M.confirmedExpired : M.confirmedAt).replace("{at}", mdTime(done.at))}</b>
          <span className="muted num" data-testid="notice-record-no">{M.recordNo.replace("{no}", done.recordNo)}</span>
        </div>
      ) : d.disputed ? (
        <div className="oaccept-done" data-testid="notice-disputed">
          <b>{say(M.disputedNote).replace("{at}", mdTime(d.disputed.at))}</b>
        </div>
      ) : (
        <div className="oaccept-bar" data-testid="notice-confirm-bar" data-notice-above="">
          {d.sender ? <p className="hint" data-testid="notice-sender">{M.sender}</p> : <p className="oaccept-first">{M.note}</p>}
          <ErrorText>{err}</ErrorText>
          <button type="button" className="btn primary oaccept-btn" disabled={busy || d.sender} onClick={() => void confirm()} data-testid="notice-confirm-btn">
            {busy ? M.confirming : M.confirm}
          </button>
          {d.sender ? null : (
            <button type="button" className="btn text" onClick={() => setAsk({ reason: "" })} data-testid="notice-dispute-ask">{M.dispute}</button>
          )}
        </div>
      )}
      <footer className="dview-foot">
        <img src={P.assets.logoHeader} alt={P.name} width={96} height={30} />
      </footer>
      {ask ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && setAsk(null)}>
          <div className="dlg" role="dialog" aria-label={M.disputeTitle} data-testid="notice-dispute-dialog">
            <div className="dh">{M.disputeTitle}</div>
            <div className="db">
              <label className="olbl" htmlFor="nd_reason">{M.disputeLabel}</label>
              <textarea id="nd_reason" className="inp" rows={4} maxLength={PAYMENT_NOTICE_LIMITS.reasonMax} value={ask.reason} onChange={(e) => setAsk({ reason: e.target.value })} data-testid="notice-dispute-reason" />
              <ErrorText>{ask.error}</ErrorText>
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn quiet" disabled={busy} onClick={() => setAsk(null)}>{M.disputeClose}</button>
              <button type="button" className="btn primary" disabled={busy || !ask.reason.trim()} onClick={() => void dispute()} data-testid="notice-dispute-send">{M.disputeSend}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
